import React, { useEffect, useState } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { useCompare } from "../contexts/CompareContext";
import { api } from "../lib/api";
import { fmtCHF } from "../lib/constants";
import { GitCompareArrows, X, ArrowRight } from "lucide-react";

/**
 * Sticky compare bar — appears at the bottom once >=1 vehicle is selected.
 * Hidden on /compare itself (no point in showing).
 */
export default function CompareBar() {
  const { ids, count, remove, clear, max } = useCompare();
  const navigate = useLocaleNavigate();
  const [previews, setPreviews] = useState({}); // id -> {title, price_chf, image}

  // Hide bar on the compare page itself
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const onComparePage = /\/compare(\b|\?|$)/.test(path);

  // Fetch tiny previews for selected ids (parallel)
  useEffect(() => {
    if (count === 0) return;
    let alive = true;
    Promise.all(
      ids.filter((id) => !previews[id]).map((id) =>
        api.getListing(id).then((l) => ({ id, l })).catch(() => null)
      )
    ).then((results) => {
      if (!alive) return;
      const next = { ...previews };
      for (const r of results) {
        if (r && r.l) next[r.id] = {
          title: r.l.title,
          price_chf: r.l.price_chf,
          image: (r.l.images && r.l.images[0]) || null,
          brand: r.l.brand,
          model: r.l.model,
        };
      }
      setPreviews(next);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(",")]);

  if (count === 0 || onComparePage) return null;

  const goCompare = () => {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("id", id));
    navigate(`/compare?${params.toString()}`);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-md"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="compare-bar"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <div className="hidden shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-red-500 sm:flex">
          <GitCompareArrows className="h-4 w-4" />
          Vergleich · {count}/{max}
        </div>

        {/* Mini previews */}
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
          {ids.map((id) => {
            const p = previews[id];
            return (
              <div
                key={id}
                className="group relative flex shrink-0 items-center gap-2 rounded-lg border border-zinc-800 bg-black px-2 py-1.5 pr-7"
                data-testid={`compare-bar-item-${id}`}
              >
                {p?.image ? (
                  <img src={p.image} alt="" className="h-8 w-12 rounded object-cover" />
                ) : (
                  <div className="h-8 w-12 rounded bg-zinc-900" />
                )}
                <div className="min-w-0 max-w-[140px]">
                  <p className="truncate text-[11px] font-semibold text-white">
                    {p ? `${p.brand} ${p.model}` : "…"}
                  </p>
                  <p className="text-[10px] text-zinc-500">
                    {p?.price_chf ? fmtCHF(p.price_chf) : ""}
                  </p>
                </div>
                <button
                  onClick={() => remove(id)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-white"
                  title="Entfernen"
                  data-testid={`compare-bar-remove-${id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={clear}
            className="hidden text-xs text-zinc-500 hover:text-zinc-300 sm:inline"
            data-testid="compare-bar-clear"
          >
            Alle entfernen
          </button>
          <button
            onClick={goCompare}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500"
            data-testid="compare-bar-go"
          >
            Vergleichen <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
